import React, { useEffect, useState } from 'react';
import { useNotification } from '../../../../context/NotificationContext';
import {
  getPlatformSendGridSettings,
  setPlatformSendGridSettings,
  testPlatformSendGridSettings,
  clearPlatformSendGridSettings,
} from './sendgridCredentialsApi';
const EMPTY_FORM = {
  apiKey: '',
  fromEmail: '',
  fromName: '',
  contactToEmail: '',
};

const SendGridCredentialsCard = () => {
  const { showToast } = useNotification();
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ configured: false, apiKeyLast4: '' });
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('idle');
  const [error, setError] = useState('');

  const applyStatus = (data) => {
    setStatus({
      configured: Boolean(data?.configured),
      apiKeyLast4: data?.apiKeyLast4 || '',
    });
    setForm((prev) => ({
      ...prev,
      apiKey: '',
      fromEmail: data?.fromEmail || '',
      fromName: data?.fromName || '',
      contactToEmail: data?.contactToEmail || '',
    }));
  };

  useEffect(() => {
    let cancelled = false;
    getPlatformSendGridSettings()
      .then((data) => {
        if (!cancelled) applyStatus(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err?.message || 'Failed to load SendGrid settings';
          setError(message);
          showToast(message, 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setAction('saving');
    setError('');
    showToast('Saving SendGrid credentials…', 'info');
    try {
      const data = await setPlatformSendGridSettings({
        apiKey: form.apiKey.trim(),
        fromEmail: form.fromEmail.trim(),
        fromName: form.fromName.trim(),
        contactToEmail: form.contactToEmail.trim(),
      });
      applyStatus(data);
      showToast('SendGrid credentials saved.', 'success');
    } catch (err) {
      const message = err?.message || 'Failed to save SendGrid settings';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setLoading(true);
    setAction('testing');
    setError('');
    showToast('Sending test email…', 'info');
    try {
      const data = await testPlatformSendGridSettings();
      showToast(`Test email sent to ${data?.to || 'your admin account'}.`, 'success');
    } catch (err) {
      const message = err?.message || 'SendGrid test failed';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Remove the stored SendGrid API key? Email sending will stop until you save new credentials.')) {
      return;
    }
    setLoading(true);
    setAction('clearing');
    setError('');
    showToast('Removing SendGrid credentials…', 'info');
    try {
      await clearPlatformSendGridSettings();
      applyStatus({ configured: false, fromEmail: '', fromName: '', contactToEmail: '', apiKeyLast4: '' });
      setForm(EMPTY_FORM);
      showToast('SendGrid credentials removed.', 'success');
    } catch (err) {
      const message = err?.message || 'Failed to disconnect SendGrid';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  return (
    <section className="admin-card app-settings-card platform-credentials-card">
      <div className="platform-credentials-card__header">
        <div>
          <h2 className="platform-credentials-card__title">SendGrid</h2>
          <p className="platform-credentials-card__desc">
            Platform email credentials used for booking notices, invites, and admin mail.
          </p>
        </div>
        <span className={`platform-credentials-status ${status.configured ? 'is-connected' : ''}`}>
          {status.configured ? 'Connected' : 'Not connected'}
        </span>
      </div>

      {error && (
        <div className="pkg-dialog-alert pkg-dialog-alert--error" role="alert">
          {error}
        </div>
      )}

      <form className="platform-credentials-form" onSubmit={handleSave}>
        <div className="pkg-dialog-field">
          <label htmlFor="sendgrid-api-key">API key</label>
          <input
            id="sendgrid-api-key"
            className="pkg-dialog-input"
            type="password"
            value={form.apiKey}
            onChange={handleChange('apiKey')}
            placeholder={status.configured ? 'Leave blank to keep the saved key' : 'SG.••••'}
            autoComplete="new-password"
          />
        </div>
        <div className="pkg-dialog-row">
          <div className="pkg-dialog-field">
            <label htmlFor="sendgrid-from-email">From email</label>
            <input
              id="sendgrid-from-email"
              className="pkg-dialog-input"
              type="email"
              value={form.fromEmail}
              onChange={handleChange('fromEmail')}
              placeholder="notify@neatschedule.com"
              autoComplete="off"
            />
          </div>
          <div className="pkg-dialog-field">
            <label htmlFor="sendgrid-from-name">From name</label>
            <input
              id="sendgrid-from-name"
              className="pkg-dialog-input"
              type="text"
              value={form.fromName}
              onChange={handleChange('fromName')}
              placeholder="Neat Schedule"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="pkg-dialog-field">
          <label htmlFor="sendgrid-contact-to">Contact form inbox</label>
          <input
            id="sendgrid-contact-to"
            className="pkg-dialog-input"
            type="email"
            value={form.contactToEmail}
            onChange={handleChange('contactToEmail')}
            placeholder="contact@neatschedule.com"
            autoComplete="off"
          />
        </div>
        <div className="platform-credentials-actions">
          <button type="submit" className="pkg-btn pkg-btn-primary" disabled={loading}>
            {action === 'saving' ? 'Saving…' : 'Save credentials'}
          </button>
          <button
            type="button"
            className="pkg-btn pkg-btn-secondary"
            onClick={handleTest}
            disabled={loading || !status.configured}
          >
            {action === 'testing' ? 'Sending…' : 'Send test email'}
          </button>
          {status.configured && (
            <button
              type="button"
              className="pkg-btn pkg-btn-danger"
              onClick={handleDisconnect}
              disabled={loading}
            >
              {action === 'clearing' ? 'Removing…' : 'Disconnect'}
            </button>
          )}
        </div>
      </form>
    </section>
  );
};

export default SendGridCredentialsCard;
